import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { ContractAssignmentService } from 'src/app/shared/services/contract-assignments.service';
import { PagingTokenFixed } from '../../../shared/models/core';
import { QmAssociationNSwagMigrationService } from '../../api/migration/qm-association-nswag-migration.service';
import { QmAssociationAutoMatchSettingsViewModel } from '../../models/qm-association-auto-match-settings-dto.model';
import { QmAssociationDto, QmAssociationGridItem } from '../../models/qm-association.model';
import {
    bulkQmAssociationAssignmentChange,
    bulkQmAssociationAssignmentChangeFailure,
    bulkQmAssociationAssignmentChangeSuccess,
    bulkUnassignComplianceLiaisonFromQmAssociation,
    bulkUnassignComplianceLiaisonFromQmAssociationFailure,
    bulkUnassignComplianceLiaisonFromQmAssociationSuccess,
    bulkUpdateComplianceLiaisonFromQmAssociation,
    bulkUpdateComplianceLiaisonFromQmAssociationFailure,
    bulkUpdateComplianceLiaisonFromQmAssociationSuccess,
    createQmAssociation,
    createQmAssociationFailure,
    createQmAssociationSuccess,
    getAssociationAutomatchSettings,
    getAssociationAutomatchSettingsFailure,
    getAssociationAutomatchSettingsSuccess,
    getDocDueDate,
    getDocDueDateFailure,
    getDocDueDateSuccess,
    getQmAssociation,
    getQmAssociationClientPortalVisibilitySettings,
    getQmAssociationClientPortalVisibilitySettingsFailure,
    getQmAssociationClientPortalVisibilitySettingsSuccess,
    getQmAssociationFailure,
    getQmAssociations,
    getQmAssociationsFailure,
    getQmAssociationsSuccess,
    getQmAssociationSuccess,
    updateAssociationAutomatchSettings,
    updateAssociationAutomatchSettingsFailure,
    updateAssociationAutomatchSettingsSuccess,
    updateDocDueDate,
    updateDocDueDateFailure,
    updateDocDueDateSuccess,
    updateExpirationImplementationSettings,
    updateExpirationImplementationSettingsFailure,
    updateExpirationImplementationSettingsSuccess,
    updateIsComplete,
    updateIsCompleteFailure,
    updateIsCompleteSuccess,
    updateQmAssociationClientPortalVisibilitySettings,
    updateQmAssociationClientPortalVisibilitySettingsFailure,
    updateQmAssociationClientPortalVisibilitySettingsSuccess,
    updateQmAssociationName,
    updateQmAssociationNameFailure,
    updateQmAssociationNameSuccess
} from '../actions/qm-associations.actions';
import { ComplianceState } from '../state/compliance.state';
import { QmAssociationsClient } from '../../api/qm-associations-client.service';

@Injectable()
export class QmAssociationsEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _store: Store<ComplianceState>,
        private readonly _qmAssociationService: QmAssociationNSwagMigrationService,
        private readonly _contractAssignmentService: ContractAssignmentService,
        private readonly _qmAssociationClient: QmAssociationsClient
    ) {}

    bulkQmAssociationAssignmentChange$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkQmAssociationAssignmentChange),
            exhaustMap((action) => {
                return this._qmAssociationService
                    .updateQmAssociationAssignment(action.bulkQmAssociationAssignmentChange)
                    .pipe(
                        map(() => bulkQmAssociationAssignmentChangeSuccess()),
                        catchError((error) => of(bulkQmAssociationAssignmentChangeFailure({ error })))
                    );
            })
        )
    );

    bulkQmAssociationAssignmentChangeSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkQmAssociationAssignmentChangeSuccess),
            map(() => showToasterMessage({ message: 'Saved facilities assignment successfully' }))
        )
    );

    createQmAssociation$ = createEffect(() =>
        this._actions$.pipe(
            ofType(createQmAssociation),
            switchMap((action) => {
                return this._qmAssociationService.createQmAssociation(action.association).pipe(
                    map(() => createQmAssociationSuccess()),
                    catchError((result) => of(createQmAssociationFailure({ error: result })))
                );
            })
        )
    );

    createQmAssociationSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(createQmAssociationSuccess),
            map(() => showToasterMessage({ message: 'QM Association saved successfully' }))
        )
    );

    createQmAssociationFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(createQmAssociationFailure),
            map(() => showToasterMessage({ message: 'Error: QM Association was not saved' }))
        )
    );

    updateQmAssociationName$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateQmAssociationName),
            switchMap((action) => {
                const id = action.id;
                return this._qmAssociationService.updateQmAssociationName(action.id, action.association).pipe(
                    map(() => updateQmAssociationNameSuccess({ id })),
                    catchError((result) => of(updateQmAssociationNameFailure({ error: result })))
                );
            })
        )
    );

    updateQmAssociationNameSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateQmAssociationNameSuccess),
            map(() => showToasterMessage({ message: 'QM Association name saved successfully' }))
        )
    );

    updateQmAssociationNameFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateQmAssociationNameFailure),
            map(() => showToasterMessage({ message: 'Error: QM Association name was not saved' }))
        )
    );

    getQmAssociations$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getQmAssociations),
            switchMap((action) => {
                return this._qmAssociationService.getQmAssociations(action.args).pipe(
                    map((result: PagingTokenFixed<QmAssociationGridItem[]>) =>
                        getQmAssociationsSuccess({ associations: result })
                    ),
                    catchError((result) => of(getQmAssociationsFailure({ error: result })))
                );
            })
        )
    );

    getQmAssociation$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getQmAssociation, updateQmAssociationNameSuccess),
            switchMap((action) => {
                return this._qmAssociationService.getQmAssociation(action.id).pipe(
                    map((result: QmAssociationDto) => getQmAssociationSuccess({ association: result })),
                    catchError((result) => of(getQmAssociationFailure({ error: result })))
                );
            })
        )
    );

    getClientPortalVisibilitySettings$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getQmAssociationClientPortalVisibilitySettings),
            switchMap((action) => {
                return this._qmAssociationService.getClientPortalVisibility(action.id).pipe(
                    map((result) =>
                        getQmAssociationClientPortalVisibilitySettingsSuccess({
                            clientPortalVisibilitySettings: result
                        })
                    ),
                    catchError((result) => of(getQmAssociationClientPortalVisibilitySettingsFailure({ error: result })))
                );
            })
        )
    );

    updateClientPortalVisibilitySettings$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateQmAssociationClientPortalVisibilitySettings),
            switchMap((action) => {
                return this._qmAssociationService.updateClientPortalVisibility(action.id, action.updates).pipe(
                    map(() => updateQmAssociationClientPortalVisibilitySettingsSuccess({ id: action.id })),
                    catchError((result) =>
                        of(updateQmAssociationClientPortalVisibilitySettingsFailure({ error: result }))
                    )
                );
            })
        )
    );

    updateClientPortalVisibilitySettingsSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateQmAssociationClientPortalVisibilitySettingsSuccess),
            tap((action) => this._store.dispatch(getQmAssociationClientPortalVisibilitySettings({ id: action.id }))),
            map(() => showToasterMessage({ message: 'Saved Successfully' }))
        )
    );

    getDocDueDate$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getDocDueDate, updateDocDueDateSuccess),
            switchMap((action) => {
                return this._qmAssociationService.getDocDueDate(action.id).pipe(
                    map((docDueDate) => getDocDueDateSuccess({ docDueDate })),
                    catchError((result) => of(getDocDueDateFailure({ error: result })))
                );
            })
        )
    );

    updateDocDueDate$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateDocDueDate),
            tap(() => {
                this._store.dispatch(showToasterMessage({ message: 'Successfully updated doc due date.' }));
            }),
            switchMap((action) => {
                const id = action.id;
                return this._qmAssociationService.updateDocDueDate(action.id, action.docDueDate).pipe(
                    map(() => updateDocDueDateSuccess({ id })),
                    catchError((result) => of(updateDocDueDateFailure({ error: result })))
                );
            })
        )
    );

    updateIsComplete$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateIsComplete),
            tap(() => {
                this._store.dispatch(
                    showToasterMessage({ message: 'Successfully updated qualification mapping status.' })
                );
            }),
            switchMap((action) => {
                const id = action.id;
                return this._qmAssociationService.updateCompleteStatus(action.id, action.isComplete).pipe(
                    map(() => {
                        this._store.dispatch(getQmAssociation({ id }));
                        return updateIsCompleteSuccess({ id });
                    }),
                    catchError((result) => of(updateIsCompleteFailure({ error: result })))
                );
            })
        );
    });

    updateExpirationImplementationSettings$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateExpirationImplementationSettings),
            switchMap((action) => {
                return this._qmAssociationClient
                    .updateExpirationImplementationSettings(action.id, {
                        entityId: action.id,
                        expirationImplementationStatus: action.expirationImplementationStatus
                    })
                    .pipe(
                        map(() => updateExpirationImplementationSettingsSuccess({ id: action.id })),
                        catchError((result) => of(updateExpirationImplementationSettingsFailure({ error: result })))
                    );
            })
        );
    });

    updateExpirationImplementationSettingsSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateExpirationImplementationSettingsSuccess),
            map(() => showToasterMessage({ message: 'Updated Expiration Implementation Settings successfully' }))
        )
    );

    updateExpirationImplementationSettingsFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateExpirationImplementationSettingsFailure),
            map(() => showToasterMessage({ message: 'Failed to update Expiration Implementation Settings' }))
        )
    );

    bulkUpdateComplianceLiaisonFromQmAssociation$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUpdateComplianceLiaisonFromQmAssociation),
            exhaustMap((action) => {
                return this._contractAssignmentService.updateBulkAssignmentConfiguration(action.bulkAssignment).pipe(
                    map(() => bulkUpdateComplianceLiaisonFromQmAssociationSuccess()),
                    catchError((error: unknown) => of(bulkUpdateComplianceLiaisonFromQmAssociationFailure({ error })))
                );
            })
        )
    );

    bulkUnassignComplianceLiaisonFromQmAssociation$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUnassignComplianceLiaisonFromQmAssociation),
            exhaustMap((action) => {
                return this._contractAssignmentService
                    .updateExpireAssignmentConfiguration(action.bulkUnassignAssignment)
                    .pipe(
                        map(() => bulkUnassignComplianceLiaisonFromQmAssociationSuccess()),
                        catchError((error: unknown) =>
                            of(bulkUnassignComplianceLiaisonFromQmAssociationFailure({ error }))
                        )
                    );
            })
        )
    );

    showBulkUnassignCLSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUnassignComplianceLiaisonFromQmAssociationSuccess),
            map(() => showToasterMessage({ message: 'Successfully bulk unassigned Compliance Liaison.' }))
        )
    );

    showBulkUnassignCLFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUnassignComplianceLiaisonFromQmAssociationFailure),
            map(() => showToasterMessage({ message: 'Failed to bulk unassign Compliance Liaison.' }))
        )
    );

    showBulkAssignCLSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUpdateComplianceLiaisonFromQmAssociationSuccess),
            map(() => showToasterMessage({ message: 'Successfully bulk assigned Compliance Liaison.' }))
        )
    );

    showBulkAssignCLFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUpdateComplianceLiaisonFromQmAssociationFailure),
            map(() => showToasterMessage({ message: 'Failed to bulk assign Compliance Liaison.' }))
        )
    );

    getAssociationAutomatchSettings$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getAssociationAutomatchSettings),
            switchMap((action) => {
                return this._qmAssociationService.getAutoMatchSettings(action.associationId).pipe(
                    map((settings: QmAssociationAutoMatchSettingsViewModel) =>
                        getAssociationAutomatchSettingsSuccess({ settings })
                    ),
                    catchError((error) => of(getAssociationAutomatchSettingsFailure({ error })))
                );
            })
        )
    );

    updateAssociationAutomatchSettings$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateAssociationAutomatchSettings),
            switchMap((action) => {
                return this._qmAssociationService.updateAutoMatchSettings(action.settings).pipe(
                    map(() => updateAssociationAutomatchSettingsSuccess({ settings: action.settings })),
                    catchError((error) => of(updateAssociationAutomatchSettingsFailure({ error })))
                );
            })
        )
    );
}
